/**
 * ISS-123: groups are two levels, a production (no parent) and its sites (the
 * production as parent). The server refuses any other parent: a site, the
 * group itself, another company's group. These keep the bulk editor to what
 * the server accepts.
 */
export interface ParentCandidate {
  id: string;
  company_id: string;
  parent_group_id: string | null;
}

/**
 * The parents the bulk "Move under parent" picker may offer for the selected
 * groups: top-level productions of the one company every selected group is
 * in, never a selected group. None when the selection spans companies.
 */
export function bulkParentOptions<T extends ParentCandidate>(all: T[], selectedIds: ReadonlySet<string>): T[] {
  const companies = new Set(all.filter((g) => selectedIds.has(g.id)).map((g) => g.company_id));
  if (companies.size !== 1) return [];
  const [companyId] = companies;
  return all.filter((g) => !g.parent_group_id && !selectedIds.has(g.id) && g.company_id === companyId);
}

/**
 * A bulk update's per-group results → the error to show, or null when all
 * went through. Each group is its own request, so some may land and some not.
 */
export function bulkFailureMessage(results: PromiseSettledResult<unknown>[]): string | null {
  const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed.length === 0) return null;
  const reason = failed[0].reason instanceof Error ? failed[0].reason.message : 'Update failed';
  return `${failed.length} of ${results.length} not updated: ${reason}`;
}
